import { App, Button, Space, Tag } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type ActiveAlertItem from '../../../interfaces/ActiveAlertItem';
import { useAppStore } from '../../../store/store';
import {
  alertSeverityToAntdType,
  alertSupportsTotpCta,
  getAlertMessage,
  normalizeAlertSeverity,
} from '../../../utils/alertMessages';
import { openTotpFormModal } from '../../../utils/openTotpFormModal';
import ListyMetaRow from '../../Common/ListyMetaRow';
import ListyWithEmpty from '../../Common/ListyWithEmpty';
import AlertDismissForm from '../AlertDismissForm';

export interface AlertsListFormProps {
  onAllCleared: () => void;
  getUserInfo?: () => void | Promise<void>;
}

/**
 * Lists all active server alerts with per-row actions (2FA modal, dismiss/snooze).
 */
export default function AlertsListForm({ onAllCleared, getUserInfo }: AlertsListFormProps) {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const alerts = useAppStore((s) => s.activeAlerts);
  const prevCountRef = useRef(alerts.length);

  useEffect(() => {
    if (prevCountRef.current > 0 && alerts.length === 0) {
      onAllCleared();
    }
    prevCountRef.current = alerts.length;
  }, [alerts.length, onAllCleared]);

  const openDismiss = (alert: ActiveAlertItem): void => {
    const inst = modal.confirm({
      title: t('modal.alertDismiss.title'),
      content: (
        <AlertDismissForm
          alert={alert}
          onSuccess={() => inst.destroy()}
          onCancel={() => inst.destroy()}
        />
      ),
      footer: null,
      width: 480,
    });
  };

  const openTotp = (): void => {
    openTotpFormModal({ modal, t, getUserInfo });
  };

  return (
    <div className="max-h-[60vh] overflow-y-auto">
      <ListyWithEmpty
        items={alerts}
        rowKey="code"
        emptyText={t('common.noData')}
        itemRender={(item) => (
          <ListyMetaRow
            className="py-0 [&>:first-child]:hidden"
            avatar={null}
            title={
              <Space size="small" wrap>
                <Tag color={severityToTagColor(item.severity as string)}>
                  {normalizeAlertSeverity(item.severity as string)}
                </Tag>
                <span className="text-sm font-medium">{item.code}</span>
              </Space>
            }
            description={<span className="text-sm">{getAlertMessage(item, t)}</span>}
            actions={
              alertSupportsTotpCta(item.code) || item.dismissable ? (
                <>
                  {alertSupportsTotpCta(item.code) ? (
                    <Button
                      size="small"
                      type="link"
                      className="text-blue-dark! hover:text-azure!"
                      onClick={openTotp}
                    >
                      {t('common.alerts.manageTwoFactor')}
                    </Button>
                  ) : null}
                  {item.dismissable ? (
                    <Button
                      size="small"
                      type="link"
                      className="text-blue-dark! hover:text-azure!"
                      onClick={() => openDismiss(item)}
                    >
                      {t('modal.alertsList.dismiss')}
                    </Button>
                  ) : null}
                </>
              ) : undefined
            }
          />
        )}
      />
    </div>
  );
}

function severityToTagColor(severity: string): string {
  const antd = alertSeverityToAntdType(severity);
  if (antd === 'error') {
    return 'red';
  }
  if (antd === 'warning') {
    return 'gold';
  }
  return 'blue';
}
